import { create } from "zustand";

// SPA-side mirror of the BFF session. The browser never sees the
// access token itself; we only know it exists (and when it expires).
// /auth/me is the source of truth and is called on bootstrap +
// after every auth state change (login, logout, refresh).

export interface AuthProfile {
  sub: string;
  preferredUsername?: string;
  email?: string;
  name?: string;
  givenName?: string;
  familyName?: string;
  locale?: string;
  // Keycloak authorities forwarded by the BFF. UI-gating only - genesis
  // enforces the real permission server-side. Absent/empty for a plain user.
  roles?: string[];
}

// Why a live session ended, as reported by /auth/me. "expired" - the refresh
// token reached its deadline. "tokens-lost" - it went missing while that
// deadline was still in the future (BFF restart with an in-memory store, Valkey
// flush, back-channel logout), so no time limit was reached and saying one was
// would be a lie. Only "tokens-lost" is worth a silent recovery attempt.
export type SessionEndedReason = "expired" | "tokens-lost";

interface AuthState {
  /** Set to `null` before the first /auth/me has completed. */
  status: "loading" | "authenticated" | "anonymous";
  profile: AuthProfile | null;
  accessTokenExpiresAt: number | null; // unix seconds, opaque count for the SPA
  // Unix seconds, or null when the BFF has no usable value. This is the SESSION
  // deadline (Keycloak's sliding SSO idle window), not the access token's - the
  // two are routinely 15 minutes apart and only this one means "logged out".
  // Reported for diagnostics and display; never a logout trigger on its own,
  // because it slides outward on every silent refresh the SPA cannot observe.
  sessionExpiresAt: number | null;
  // True only when a session that WAS live has ended underneath the user, as
  // opposed to arriving anonymous. It is what separates "please sign in" from
  // "your work is no longer being saved", so it drives the notice rather than
  // `status` alone. Deliberately one-way: the notice's only exit is signing in
  // again, which tears the session down and leaves the page (#177), so there is
  // no "dismiss" action to clear it in place.
  sessionExpired: boolean;
  /** Which story the notice tells. Null until a session actually ends. */
  sessionEndedReason: SessionEndedReason | null;

  setAuthenticated: (args: {
    profile: AuthProfile;
    accessTokenExpiresAt: number;
    sessionExpiresAt: number | null;
  }) => void;
  setAnonymous: () => void;
  markSessionExpired: (reason?: SessionEndedReason) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: "loading",
  profile: null,
  accessTokenExpiresAt: null,
  sessionExpiresAt: null,
  sessionExpired: false,
  sessionEndedReason: null,

  setAuthenticated: (args) =>
    set({
      status: "authenticated",
      profile: args.profile,
      accessTokenExpiresAt: args.accessTokenExpiresAt,
      sessionExpiresAt: args.sessionExpiresAt,
      sessionExpired: false,
      sessionEndedReason: null,
    }),

  setAnonymous: () =>
    set({
      status: "anonymous",
      profile: null,
      accessTokenExpiresAt: null,
      sessionExpiresAt: null,
    }),

  markSessionExpired: (reason = "expired") =>
    set({ sessionExpired: true, sessionEndedReason: reason }),
}));
